import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Column } from '@ant-design/charts';
import type { GroupAnalyticsParams, ModeMixRow } from '@/types';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';

const { Text } = Typography;

const MODE_LABELS: Record<string, string> = {
  quick_play: 'Quick Play',
  custom_match: 'Custom Match',
  blitz_showdown: 'Blitz Showdown',
};

/**
 * The only modes priced in match tokens. Every other mode reports a token
 * cost of 0 by rule (Quick Play costs coins), so that 0 is shown as "not
 * priced in tokens" rather than as a real zero.
 */
const TOKEN_MODES = new Set(['custom_match', 'blitz_showdown']);

/**
 * Overs is unbounded in Custom Match, so the chart keeps the busiest overs
 * settings and folds the rest into one series. The detail table lists all.
 */
const MAX_OVERS_SERIES = 6;
const OTHER_OVERS = 'Other overs';
const UNKNOWN_OVERS = 'Overs not recorded';

const titleCase = (raw: string): string =>
  raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const modeLabel = (matchType: string): string =>
  MODE_LABELS[matchType] ?? (titleCase(matchType ?? '') || 'Unknown mode');

const oversLabel = (overs?: number): string =>
  overs == null ? UNKNOWN_OVERS : `${overs} ${overs === 1 ? 'over' : 'overs'}`;

const fmt = (n: number): string => n.toLocaleString('en-IN');

/** Percentage of d, or null when there is nothing to divide by. */
const shareOf = (n: number, d: number): number | null => (d > 0 ? (n / d) * 100 : null);

const fmtShare = (v: number | null): string => (v === null ? '–' : `${v.toFixed(1)}%`);

/** Sort helper that keeps nulls at the bottom in ascending order. */
const byNullable = (a: number | null, b: number | null): number =>
  (a ?? Number.NEGATIVE_INFINITY) - (b ?? Number.NEGATIVE_INFINITY);

const renderTokenCost = (matchType: string, tokenCost: number): React.ReactNode =>
  TOKEN_MODES.has(matchType) ? (
    fmt(tokenCost)
  ) : (
    <Text type="secondary">{matchType === 'quick_play' ? 'Coins' : 'Not priced in tokens'}</Text>
  );

const renderCompleted = (completed: number, rate: number | null): React.ReactNode => (
  <span>
    {fmt(completed)} <Text type="secondary">({fmtShare(rate)})</Text>
  </span>
);

interface ModeSummary {
  matchType: string;
  label: string;
  matches: number;
  completed: number;
  tokenCost: number;
  matchShare: number | null;
  completionRate: number | null;
  tokenShare: number | null;
}

interface ModeDetail extends ModeMixRow {
  key: string;
  label: string;
  completionRate: number | null;
}

interface ChartDatum {
  mode: string;
  overs: string;
  matches: number;
}

/**
 * Which modes people play, at what length, and what the token-priced modes
 * cost by rule.
 *
 * Players are distinct per mode + overs row, so they are only shown in the
 * detail table: adding them up across overs would count a player once for
 * every overs setting they tried.
 */
export const ModeMixCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<ModeMixRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getModeMix(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load mode mix'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          matches: acc.matches + r.matches,
          completed: acc.completed + r.completed,
          tokenCost: acc.tokenCost + (TOKEN_MODES.has(r.matchType) ? r.tokenCost : 0),
        }),
        { matches: 0, completed: 0, tokenCost: 0 }
      ),
    [rows]
  );

  const summary = useMemo<ModeSummary[]>(() => {
    const byMode = new Map<string, { matches: number; completed: number; tokenCost: number }>();
    for (const r of rows) {
      const acc = byMode.get(r.matchType) ?? { matches: 0, completed: 0, tokenCost: 0 };
      acc.matches += r.matches;
      acc.completed += r.completed;
      acc.tokenCost += TOKEN_MODES.has(r.matchType) ? r.tokenCost : 0;
      byMode.set(r.matchType, acc);
    }
    return Array.from(byMode.entries())
      .map(([matchType, m]) => ({
        matchType,
        label: modeLabel(matchType),
        matches: m.matches,
        completed: m.completed,
        tokenCost: m.tokenCost,
        matchShare: shareOf(m.matches, totals.matches),
        completionRate: shareOf(m.completed, m.matches),
        tokenShare: TOKEN_MODES.has(matchType) ? shareOf(m.tokenCost, totals.tokenCost) : null,
      }))
      .sort((a, b) => b.matches - a.matches || a.label.localeCompare(b.label));
  }, [rows, totals]);

  const modeOrder = useMemo(() => Array.from(new Set(summary.map((s) => s.label))), [summary]);

  const details = useMemo<ModeDetail[]>(() => {
    const rank = new Map(summary.map((s, i) => [s.matchType, i]));
    return rows
      .map((r) => ({
        ...r,
        key: `${r.matchType}|${r.overs ?? 'none'}`,
        label: modeLabel(r.matchType),
        completionRate: shareOf(r.completed, r.matches),
      }))
      .sort(
        (a, b) =>
          (rank.get(a.matchType) ?? 0) - (rank.get(b.matchType) ?? 0) ||
          (a.overs ?? Number.POSITIVE_INFINITY) - (b.overs ?? Number.POSITIVE_INFINITY)
      );
  }, [rows, summary]);

  const chart = useMemo(() => {
    const matchesByOvers = new Map<number, number>();
    for (const r of rows) {
      if (r.overs == null) continue;
      matchesByOvers.set(r.overs, (matchesByOvers.get(r.overs) ?? 0) + r.matches);
    }
    const ranked = Array.from(matchesByOvers.entries())
      .sort((a, b) => b[1] - a[1] || a[0] - b[0])
      .map(([overs]) => overs);
    // Folding a single leftover into "Other" hides it for no gain.
    const fold = ranked.length > MAX_OVERS_SERIES + 1;
    const kept = new Set(fold ? ranked.slice(0, MAX_OVERS_SERIES) : ranked);
    const seriesOf = (overs?: number): string =>
      overs == null ? UNKNOWN_OVERS : kept.has(overs) ? oversLabel(overs) : OTHER_OVERS;

    const domain = Array.from(kept)
      .sort((a, b) => a - b)
      .map((o) => oversLabel(o));
    if (fold) domain.push(OTHER_OVERS);
    if (rows.some((r) => r.overs == null)) domain.push(UNKNOWN_OVERS);

    // Only mode + overs pairs that were actually played become segments, so
    // an absent pair is left out rather than drawn as a zero.
    const cells = new Map<string, ChartDatum>();
    for (const r of rows) {
      if (r.matches <= 0) continue;
      const mode = modeLabel(r.matchType);
      const overs = seriesOf(r.overs);
      const k = `${mode}|${overs}`;
      const cell = cells.get(k);
      if (cell) cell.matches += r.matches;
      else cells.set(k, { mode, overs, matches: r.matches });
    }
    const data = Array.from(cells.values()).sort(
      (a, b) =>
        modeOrder.indexOf(a.mode) - modeOrder.indexOf(b.mode) ||
        domain.indexOf(a.overs) - domain.indexOf(b.overs)
    );
    return { data, domain, foldedCount: fold ? ranked.length - MAX_OVERS_SERIES : 0 };
  }, [rows, modeOrder]);

  const summaryColumns = useMemo<ColumnsType<ModeSummary>>(
    () => [
      { title: 'Mode', dataIndex: 'label', key: 'label' },
      {
        title: 'Matches',
        dataIndex: 'matches',
        key: 'matches',
        align: 'right',
        render: (v: number) => fmt(v),
        sorter: (a, b) => a.matches - b.matches,
      },
      {
        title: 'Share of matches',
        dataIndex: 'matchShare',
        key: 'matchShare',
        align: 'right',
        render: (v: number | null) => fmtShare(v),
        sorter: (a, b) => byNullable(a.matchShare, b.matchShare),
      },
      {
        title: 'Completed',
        key: 'completed',
        align: 'right',
        render: (_: unknown, r) => renderCompleted(r.completed, r.completionRate),
        sorter: (a, b) => byNullable(a.completionRate, b.completionRate),
      },
      {
        title: 'Token cost',
        dataIndex: 'tokenCost',
        key: 'tokenCost',
        align: 'right',
        render: (v: number, r) => renderTokenCost(r.matchType, v),
        sorter: (a, b) => a.tokenCost - b.tokenCost,
      },
      {
        title: 'Token share',
        dataIndex: 'tokenShare',
        key: 'tokenShare',
        align: 'right',
        render: (v: number | null, r) =>
          TOKEN_MODES.has(r.matchType) ? fmtShare(v) : <Text type="secondary">–</Text>,
        sorter: (a, b) => byNullable(a.tokenShare, b.tokenShare),
      },
    ],
    []
  );

  const detailColumns = useMemo<ColumnsType<ModeDetail>>(
    () => [
      { title: 'Mode', dataIndex: 'label', key: 'label' },
      {
        title: 'Overs',
        dataIndex: 'overs',
        key: 'overs',
        align: 'right',
        render: (v?: number) => (v == null ? <Text type="secondary">Not recorded</Text> : v),
        sorter: (a, b) =>
          (a.overs ?? Number.POSITIVE_INFINITY) - (b.overs ?? Number.POSITIVE_INFINITY),
      },
      {
        title: 'Matches',
        dataIndex: 'matches',
        key: 'matches',
        align: 'right',
        render: (v: number) => fmt(v),
        sorter: (a, b) => a.matches - b.matches,
      },
      {
        title: 'Completed',
        key: 'completed',
        align: 'right',
        render: (_: unknown, r) => renderCompleted(r.completed, r.completionRate),
        sorter: (a, b) => byNullable(a.completionRate, b.completionRate),
      },
      {
        title: 'Players',
        dataIndex: 'users',
        key: 'users',
        align: 'right',
        render: (v: number) => fmt(v),
        sorter: (a, b) => a.users - b.users,
      },
      {
        title: 'Token cost',
        dataIndex: 'tokenCost',
        key: 'tokenCost',
        align: 'right',
        render: (v: number, r) => renderTokenCost(r.matchType, v),
        sorter: (a, b) => a.tokenCost - b.tokenCost,
      },
    ],
    []
  );

  return (
    <Card
      loading={loading}
      title={
        <span>
          Mode mix{' '}
          <Tooltip title="Token cost for Custom Match is computed from the entry rule (1 token per 5 overs, minimum 5 overs). For Blitz Showdown it is the tokens the server actually charged, so a price change never re-prices past matches, and a match with no recorded charge counts 0. Quick Play costs coins, so it has no token cost. Players are distinct per mode and overs row, so they are not added up across overs.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      {rows.length === 0 ? (
        !error && (
          <Text type="secondary">
            No matches were started in this range. This report counts every match by its start time
            and has data from launch onward, so an empty range means nobody played, not that data is
            missing.
          </Text>
        )
      ) : (
        <>
          <Table<ModeSummary>
            size="small"
            rowKey="matchType"
            pagination={false}
            dataSource={summary}
            columns={summaryColumns}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>All modes</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>{fmt(totals.matches)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  {fmtShare(shareOf(totals.matches, totals.matches))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  {renderCompleted(totals.completed, shareOf(totals.completed, totals.matches))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  {fmt(totals.tokenCost)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {fmtShare(shareOf(totals.tokenCost, totals.tokenCost))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          <div style={{ marginTop: 20 }}>
            <Text strong>Matches per mode by overs</Text>
            <Column
              data={chart.data}
              xField="mode"
              yField="matches"
              colorField="overs"
              stack
              height={220}
              legend={{ color: { position: 'top' } }}
              scale={{ x: { domain: modeOrder }, color: { domain: chart.domain } }}
              style={{ maxWidth: 64 }}
              axis={{ y: { labelFormatter: (v: number) => fmt(v) } }}
              tooltip={{
                items: [(d: ChartDatum) => ({ name: d.overs, value: fmt(d.matches) })],
              }}
            />
            {chart.foldedCount > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {chart.foldedCount} less-played overs settings are grouped as {OTHER_OVERS}. The
                table below lists every setting.
              </Text>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <Text strong>By overs</Text>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
              Players are distinct within each row, so they do not add up to a per-mode total.
            </div>
            <Table<ModeDetail>
              size="small"
              rowKey="key"
              dataSource={details}
              columns={detailColumns}
              pagination={details.length > 15 ? { pageSize: 10, showSizeChanger: false } : false}
            />
          </div>
        </>
      )}
    </Card>
  );
};
